import { useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINS = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
const valid = (v) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v);
const norm = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}:${d.slice(2)}` : d;
};

const Col = ({ items, sel, onPick, tid }) => {
  const ref = useRef(null);
  useEffect(() => { ref.current?.querySelector("[data-sel=true]")?.scrollIntoView({ block: "center" }); }, []);
  return (
    <div ref={ref} className="h-52 overflow-y-auto pr-1 flex flex-col gap-0.5">
      {items.map((x) => (
        <button key={x} type="button" data-sel={x === sel} onClick={() => onPick(x)} data-testid={`${tid}-${x}`}
          className={`w-12 py-1.5 rounded-md font-mono text-sm transition-colors ${x === sel ? "bg-sky-500 text-slate-950 font-bold" : "text-slate-700 hover:bg-slate-100"}`}>{x}</button>
      ))}
    </div>
  );
};

export const TimeInput = ({ value, onChange, className = "", disabled, ...rest }) => {
  const tid = rest["data-testid"] || "time";
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState(value || "");
  useEffect(() => setTxt(value || ""), [value]);
  const [h, m] = valid(value || "") ? value.split(":") : ["", ""];
  const emit = (v) => onChange({ target: { value: v } });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button type="button" {...rest} disabled={disabled}
          className={cn("flex h-10 w-full items-center gap-1.5 rounded-md border border-input bg-transparent px-2 font-mono text-sm text-left transition-colors hover:border-sky-500/60 disabled:opacity-50 disabled:cursor-not-allowed", className)}>
          <Clock className="w-3.5 h-3.5 text-sky-600 shrink-0" />
          <span className={value ? "text-slate-900" : "text-slate-500"}>{value || "--:--"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3 bg-white border-slate-200" align="start" data-testid={`${tid}-picker`}>
        <div className="flex gap-2 text-[0.625rem] font-mono text-slate-500 tracking-widest mb-1"><span className="w-12 text-center">HORA</span><span className="w-12 text-center">MIN</span></div>
        <div className="flex gap-2">
          <Col items={HOURS} sel={h} tid={`${tid}-hour`} onPick={(x) => emit(`${x}:${m || "00"}`)} />
          <Col items={MINS} sel={m} tid={`${tid}-min`} onPick={(x) => { emit(`${h || "00"}:${x}`); setOpen(false); }} />
        </div>
        <Input value={txt} inputMode="numeric" placeholder="Exacta: HH:MM" data-testid={`${tid}-exact`}
          className={`mt-2 h-8 w-[104px] font-mono text-xs ${txt && !valid(txt) ? "border-red-500/60" : ""}`}
          onChange={(e) => { const v = norm(e.target.value); setTxt(v); if (valid(v)) emit(v); }}
          onKeyDown={(e) => e.key === "Enter" && valid(txt) && setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
};
